import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup } from 'react-leaflet';
import axios from 'axios';
import { API_URL } from '../components/config.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PublicConsultationMap.css';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PublicConsultationMap = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects/public`);
      console.log('Fetched public projects for map:', response.data);
      console.log('Number of projects:', response.data.length);
      
      // Log mapData for each project
      response.data.forEach(project => {
        console.log(`Project "${project.name}":`, {
          hasMapData: !!project.mapData,
          mapData: project.mapData,
          mapDataType: typeof project.mapData
        });
      });
      
      setProjects(response.data);
      setError('');
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load consultation projects');
    } finally {
      setLoading(false);
    }
  };

  // Filter and parse projects with valid location data
  const projectsWithLocations = projects.filter(project => {
    if (!project.mapData) {
      console.log(`Project "${project.name}" has no mapData`);
      return false;
    }
    try {
      const parsed = typeof project.mapData === 'string' ? JSON.parse(project.mapData) : project.mapData;
      console.log(`Parsed mapData for "${project.name}":`, parsed);
      
      const hasValidData = parsed && (
        parsed.type === 'Point' || 
        parsed.type === 'Polygon' || 
        parsed.type === 'LineString' || 
        parsed.type === 'MultiPolygon' ||
        parsed.type === 'MultiPoint' ||
        parsed.type === 'MultiLineString' ||
        (parsed.coordinates && Array.isArray(parsed.coordinates))
      );
      
      console.log(`Project "${project.name}" has valid location:`, hasValidData);
      return hasValidData;
    } catch (e) {
      console.error('Error parsing mapData for:', project.name, e);
      return false;
    }
  });

  console.log('Projects with valid locations:', projectsWithLocations.length);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'completed': return '#2196F3';
      default: return '#999';
    }
  };

  // Style function for GeoJSON features
  const getFeatureStyle = (project) => ({
    color: getStatusColor(project.status),
    weight: 3,
    opacity: 0.8,
    fillOpacity: 0.35
  });

  // Render marker or GeoJSON based on data type
  const renderProjectOnMap = (project) => {
    try {
      const mapData = typeof project.mapData === 'string' ? JSON.parse(project.mapData) : project.mapData;
      
      // Handle Point geometry (marker)
      if (mapData.type === 'Point') {
        const [lng, lat] = mapData.coordinates;
        return (
          <Marker key={project._id} position={[lat, lng]}>
            <Popup>
              <div className="map-popup">
                <h3>{project.name}</h3>
                <div className={`status-badge status-${project.status}`}>
                  {project.status}
                </div>
                <p>{project.description.substring(0, 150)}...</p>
                <div className="popup-meta">
                  <span>💬 {project.commentCount || 0} comments</span>
                </div>
                <button 
                  className="popup-button"
                  onClick={() => navigate(`/consultation/project/${project._id}`)}
                >
                  View Details & Comment →
                </button>
              </div>
            </Popup>
          </Marker>
        );
      }
      
      // Handle other GeoJSON types (Polygon, LineString, etc.)
      if (mapData.type) {
        return (
          <GeoJSON
            key={project._id}
            data={mapData}
            style={getFeatureStyle(project)}
            onEachFeature={(feature, layer) => {
              layer.bindPopup(`
                <div class="map-popup">
                  <h3>${project.name}</h3>
                  <div class="status-badge status-${project.status}">
                    ${project.status}
                  </div>
                  <p>${project.description.substring(0, 150)}...</p>
                  <div class="popup-meta">
                    <span>💬 ${project.commentCount || 0} comments</span>
                  </div>
                  <button 
                    class="popup-button"
                    onclick="window.location.href='/consultation/project/${project._id}'"
                  >
                    View Details & Comment →
                  </button>
                </div>
              `);
            }}
          />
        );
      }
    } catch (e) {
      console.error('Error rendering project on map:', project.name, e);
      return null;
    }
  };

  if (loading) {
    return (
      <div className="public-consultation-map">
        <div className="page-header">
          <h2>🗺️ Community Consultation Map</h2>
          <button className="back-button" onClick={() => navigate('/consultation')}>
            ← Back to List
          </button>
        </div>
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading consultation projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-consultation-map">
        <div className="page-header">
          <h2>🗺️ Community Consultation Map</h2>
          <button className="back-button" onClick={() => navigate('/consultation')}>
            ← Back to List
          </button>
        </div>
        <div className="error-box">
          <p>{error}</p>
          <button onClick={fetchProjects}>Try Again</button>
        </div>
      </div>
    );
  }

  if (projectsWithLocations.length === 0) {
    return (
      <div className="public-consultation-map">
        <div className="page-header">
          <h2>🗺️ Community Consultation Map</h2>
          <button className="back-button" onClick={() => navigate('/consultation')}>
            ← Back to List
          </button>
        </div>
        <div className="no-locations">
          <div className="no-locations-icon">📍</div>
          <h3>No Project Locations Available</h3>
          <p>Projects don't have location data yet. Check back soon or view the project list.</p>
          <button onClick={() => navigate('/consultation')}>View Project List</button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-consultation-map">
      <div className="page-header">
        <h2>🗺️ Community Consultation Map</h2>
        <button className="back-button" onClick={() => navigate('/consultation')}>
          ← Back to List
        </button>
      </div>

      <div className="map-info">
        <p>
          Showing {projectsWithLocations.length} of {projects.length} consultation projects on the map.
          Click on markers for details and to share your feedback.
        </p>
      </div>

      <div className="map-content">
        <div className="map-legend">
          <h4>Legend</h4>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#4CAF50' }}></span>
              <span>Active Projects</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#2196F3' }}></span>
              <span>Completed Projects</span>
            </div>
          </div>
        </div>

        <div className="map-container-wrapper">
          <MapContainer
            center={[48.48985044397585, -89.24318011520745]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {projectsWithLocations.map(project => renderProjectOnMap(project))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default PublicConsultationMap;
